import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  API,
  authConfig,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  alertError,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import { TicketDetailLink } from '../ticketNav';
import { ModuleShell, SkeletonBlock, FilterChips } from '../../../components/module-ui';

const AUDIT = `${API}/audit`;

const ACTION_COLORS = {
  created: '#2563EB', status_changed: '#F59E0B', assigned: '#10B981',
  transferred: '#8B5CF6', closed: '#6B7280', reopened: '#EF4444',
  comment_added: '#0EA5E9', closure_verified: '#059669', closure_rejected: '#DC2626',
  bulk_assign: '#10B981', bulk_close: '#6B7280', timing_updated: '#A16207',
};
const actionColor = (a) => ACTION_COLORS[a] || '#374151';
const prettyAction = (a) => (a || '').replace(/_/g, ' ');

const GROUPS = [
  { key: 'product', label: 'By Product' },
  { key: 'team', label: 'By Team' },
  { key: 'actor', label: 'By Team Member' },
];

const emptyFilters = { product_id: '', team_id: '', actor_id: '', action: '', date_from: '', date_to: '' };

export default function AuditLogPage() {
  const [view, setView] = useState('summary');      // 'summary' | 'events'
  const [groupBy, setGroupBy] = useState('product');
  const [filters, setFilters] = useState(emptyFilters);
  const [options, setOptions] = useState({ actors: [], actions: [], products: [], teams: [] });
  const [summary, setSummary] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${AUDIT}/filters`, authConfig())
      .then((r) => setOptions(r.data || { actors: [], actions: [], products: [], teams: [] }))
      .catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const loadSummary = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${AUDIT}/summary`, authConfig({ ...params(), group_by: groupBy }));
      setSummary(r.data || []);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load summary'); }
    finally { setLoading(false); }
  }, [groupBy, params]);

  const loadEvents = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${AUDIT}/events`, authConfig(params()));
      setEvents(r.data || []);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load events'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => {
    if (view === 'summary') loadSummary(); else loadEvents();
  }, [view, loadSummary, loadEvents]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  // Drill from a summary row into the filtered event feed.
  const drillInto = (row) => {
    const patch = {};
    if (groupBy === 'product') patch.product_id = row.key === '__none__' ? '' : row.key;
    if (groupBy === 'team') patch.team_id = row.key === '__none__' ? '' : row.key;
    if (groupBy === 'actor') patch.actor_id = row.key === '__none__' ? '' : row.key;
    setFilters((f) => ({ ...f, ...patch }));
    setView('events');
  };

  const filterChips = [];
  if (filters.product_id) {
    const label = options.products.find((p) => String(p.product_id) === String(filters.product_id))?.name || filters.product_id;
    filterChips.push({ id: 'product', label: `Product: ${label}`, onRemove: () => setF({ product_id: '' }) });
  }
  if (filters.team_id) {
    const label = options.teams.find((t) => String(t.team_id) === String(filters.team_id))?.name || filters.team_id;
    filterChips.push({ id: 'team', label: `Team: ${label}`, onRemove: () => setF({ team_id: '' }) });
  }
  if (filters.actor_id) {
    const label = options.actors.find((a) => String(a.user_id) === String(filters.actor_id))?.name || filters.actor_id;
    filterChips.push({ id: 'actor', label: `Member: ${label}`, onRemove: () => setF({ actor_id: '' }) });
  }
  if (filters.action) {
    filterChips.push({ id: 'action', label: `Action: ${prettyAction(filters.action)}`, onRemove: () => setF({ action: '' }) });
  }
  if (filters.date_from) {
    filterChips.push({ id: 'date_from', label: `From: ${filters.date_from}`, onRemove: () => setF({ date_from: '' }) });
  }
  if (filters.date_to) {
    filterChips.push({ id: 'date_to', label: `To: ${filters.date_to}`, onRemove: () => setF({ date_to: '' }) });
  }

  return (
    <ModuleShell
      title="Audit Log"
      subtitle="Who did what across the application — every create, status change, assignment, transfer, close and reopen. Slice it by product, team, or team member."
    >
    <div style={{ maxWidth: '1080px' }}>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}

      {/* view switch */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <button style={view === 'summary' ? btnPrimary : btnSecondary} onClick={() => setView('summary')}>Summary</button>
        <button style={view === 'events' ? btnPrimary : btnSecondary} onClick={() => setView('events')}>Event timeline</button>
      </div>

      {/* filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '8px', padding: '14px', border: '1px solid #E5E7EB', borderRadius: '10px', background: '#F9FAFB' }}>
        <div>
          <label style={labelStyle}>Product</label>
          <select style={fieldStyle} value={filters.product_id} onChange={(e) => setF({ product_id: e.target.value })}>
            <option value="">All products</option>
            {options.products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Team</label>
          <select style={fieldStyle} value={filters.team_id} onChange={(e) => setF({ team_id: e.target.value })}>
            <option value="">All teams</option>
            {options.teams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Team member</label>
          <select style={fieldStyle} value={filters.actor_id} onChange={(e) => setF({ actor_id: e.target.value })}>
            <option value="">Anyone</option>
            {options.actors.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Action</label>
          <select style={fieldStyle} value={filters.action} onChange={(e) => setF({ action: e.target.value })}>
            <option value="">All actions</option>
            {options.actions.map((a) => <option key={a} value={a}>{prettyAction(a)}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" style={fieldStyle} value={filters.date_from} onChange={(e) => setF({ date_from: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" style={fieldStyle} value={filters.date_to} onChange={(e) => setF({ date_to: e.target.value })} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button style={btnSecondary} onClick={() => setFilters(emptyFilters)}>Clear filters</button>
        </div>
      </div>

      <FilterChips chips={filterChips} onClearAll={() => setFilters(emptyFilters)} />

      {loading && <SkeletonBlock rows={4} />}

      {/* SUMMARY */}
      {view === 'summary' && !loading && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
            {GROUPS.map((g) => (
              <button key={g.key} style={groupBy === g.key ? btnPrimary : btnSecondary} onClick={() => setGroupBy(g.key)}>{g.label}</button>
            ))}
          </div>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
                  <th style={th}>{GROUPS.find((g) => g.key === groupBy).label.replace('By ', '')}</th>
                  <th style={th}>Events</th>
                  <th style={th}>Breakdown</th>
                  <th style={th}>Last activity</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && (
                  <tr><td style={td} colSpan={5}><span style={{ color: '#9CA3AF' }}>No activity for these filters.</span></td></tr>
                )}
                {summary.map((row) => (
                  <tr key={row.key} style={{ borderTop: '1px solid #F3F4F6' }}>
                    <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
                    <td style={td}>{row.count}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {Object.entries(row.actions).sort((a, b) => b[1] - a[1]).map(([a, c]) => (
                          <span key={a} style={{ ...pill, background: `${actionColor(a)}22`, color: actionColor(a) }}>{prettyAction(a)} {c}</span>
                        ))}
                      </div>
                    </td>
                    <td style={td}>{row.last_activity ? formatUtcToIst(row.last_activity) : '—'}</td>
                    <td style={td}><button style={linkBtn} onClick={() => drillInto(row)}>View events →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EVENTS */}
      {view === 'events' && !loading && (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#F3F4F6', textAlign: 'left' }}>
                <th style={th}>When</th>
                <th style={th}>Who</th>
                <th style={th}>Action</th>
                <th style={th}>Ticket</th>
                <th style={th}>Product / Team</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td style={td} colSpan={6}><span style={{ color: '#9CA3AF' }}>No events for these filters.</span></td></tr>
              )}
              {events.map((e) => (
                <tr key={e.audit_id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatUtcToIst(e.timestamp)}</td>
                  <td style={td}>{e.actor_name}</td>
                  <td style={td}><span style={{ ...pill, background: `${actionColor(e.action)}22`, color: actionColor(e.action) }}>{prettyAction(e.action)}</span></td>
                  <td style={td}>
                    <TicketDetailLink ticketNo={e.entity_id} style={{ color: '#002FA7' }}>{e.entity_id}</TicketDetailLink>
                    {e.ticket_subject && <div style={{ fontSize: '12px', color: '#6B7280', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.ticket_subject}</div>}
                  </td>
                  <td style={td}>
                    <div>{e.product_name || '—'}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>{e.team_name || '—'}</div>
                  </td>
                  <td style={{ ...td, fontSize: '12px', color: '#4B5563' }}>{renderDetails(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}
function renderDetails(e) {
  const d = e.details || {};
  if (e.action === 'status_changed' && (d.from || d.to)) return `${prettyAction(d.from || '?')} → ${prettyAction(d.to || '?')}`;
  if (d.assignee_names) return `to ${Array.isArray(d.assignee_names) ? d.assignee_names.join(', ') : d.assignee_names}`;
  if (d.reason) return `reason: ${d.reason}`;
  const keys = Object.keys(d);
  if (!keys.length) return '—';
  return keys.slice(0, 3).map((k) => `${k}: ${typeof d[k] === 'object' ? JSON.stringify(d[k]) : d[k]}`).join(' · ');
}

const th = { padding: '6px 8px', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em' };
const td = { padding: '6px 8px', verticalAlign: 'top' };
const pill = { display: 'inline-block', padding: '2px 8px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, textTransform: 'capitalize' };
const linkBtn = { border: 'none', background: 'none', color: '#002FA7', cursor: 'pointer', fontSize: '12px', fontWeight: 500 };

